/**
 * カード詳細編集 Drawer（Phase 23a-ext）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5
 *
 * スコープ（最小限）:
 *   - line_news / mail_schedule / mail_draft / form / mail_contacts_group / event:
 *     既存リソースを select で選択 → element.refId / refLabel / externalUrl をセット
 *   - delay: delayMs を入力
 *   - note: フリーテキスト編集
 *   - condition / metric: Phase 23b で対応予定（プレースホルダ表示）
 *
 * 設計メモ:
 *   - リソース新規作成は本フェーズ範囲外（Phase 23b で対応）
 *   - 選択は select。検索 / autocomplete は将来拡張
 *   - form 選択時のみ externalUrl に公開 URL（?campaignId 付き）を自動セット
 */
import type {
  CampaignConnection,
  CampaignElement,
  IsoOrTimestamp,
} from '@goenhub/shared';
import { REFERRAL_TAG_RE } from '@goenhub/shared';
import { Timestamp, arrayUnion, doc } from 'firebase/firestore';
import { Copy, ExternalLink, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useDocuments } from '@/hooks/useDocuments';
import { useForms } from '@/hooks/useForms';
import { useLandings } from '@/hooks/useLandings';
import { useEvents } from '@/hooks/useLineData';
import { EmailChipsInput } from '@/components/common/EmailChipsInput';
import { RefTagInput } from '@/components/common/RefTagInput';
import {
  useCreateLineNews,
  useLineNewsList,
  useScheduleLineNewsSend,
} from '@/hooks/useLineNews';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import { useMails } from '@/hooks/useMailData';
import { useMailGroups } from '@/hooks/useMailGroups';
import { useMailSchedules } from '@/hooks/useMailSchedules';
import { useLiffSettings } from '@/hooks/useSettings';
import { db } from '@/lib/firebase';
import { toDatetimeLocalValue } from '@/lib/firestoreTime';
import { safeUpdateDoc } from '@/lib/safeWrite';
import { ScheduleFormDialog } from '@/pages/mail/schedules/ScheduleFormDialog';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  element: CampaignElement | null;
  campaignId: string;
  /**
   * このカードに上流からの接続線（incoming connection）があるか。
   * mail_schedule カードで「dispatcher で audience を流し込むかどうか」を判別するために使う。
   *   - true (中間カード): targetType='addresses' の予約のみ選択可（dispatcher が arrayUnion できる）
   *   - false (起点カード): 全 targetType の予約を選択可（dispatcher は介在せず、予約自体が独立して動く）
   */
  hasIncoming?: boolean;
  /**
   * B（自動 CTA URL アサイン）用：ボード上の全要素・全接続。
   * mail/LINE カードを開いたとき、下流の landing/form を辿って ctaUrlOverride の
   * デフォルトを推定するために必要。
   */
  allElements?: CampaignElement[];
  allConnections?: CampaignConnection[];
  onSave: (next: CampaignElement) => void;
}

const TYPE_LABEL: Record<string, string> = {
  line_news: 'LINE ニュース',
  mail_schedule: '配信予約への追加',
  mail_send: 'メール配信',
  mail_draft: 'メール下書き',
  form: 'フォーム',
  landing: 'ランディング',
  referral: 'referral（タグ付き URL）',
  mail_contacts_group: '配信先グループ',
  event: 'イベント',
  document_request: '資料請求',
  briefing_request: '個別説明申込',
  quote_request: '見積依頼',
  delay: '遅延',
  condition: '分岐条件',
  metric: '計測ポイント',
  note: 'メモ',
};

const NONE_VALUE = '__none__';

interface ResourceOption {
  id: string;
  label: string;
  /** 状態バッジ等のサブラベル（任意）。例: 'scheduled' / 'sent' */
  badge?: string;
  /** true なら選択肢として残すが新規選択は不可（現在紐付き表示専用） */
  disabled?: boolean;
}

function ResourceSelector({
  type,
  value,
  onChange,
  options,
  emptyMessage,
}: {
  type: string;
  value: string | undefined;
  onChange: (id: string | undefined, label: string) => void;
  options: ResourceOption[];
  emptyMessage: string;
}) {
  return (
    <div>
      <Label className="text-xs">{TYPE_LABEL[type]} を選択</Label>
      {options.length === 0 ? (
        <p className="text-[11px] text-[var(--text-muted)] mt-1">
          {emptyMessage}
        </p>
      ) : (
        <Select
          value={value ?? NONE_VALUE}
          onValueChange={(v) => {
            if (v === NONE_VALUE) {
              onChange(undefined, '');
            } else {
              const opt = options.find((o) => o.id === v);
              onChange(v, opt?.label ?? '');
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="選択してください" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>（未設定）</SelectItem>
            {options.map((o) => (
              <SelectItem key={o.id} value={o.id} disabled={o.disabled}>
                <span className="flex items-center gap-2">
                  <span>{o.label || `(無題) ${o.id.slice(0, 6)}`}</span>
                  {o.badge && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-muted)]">
                      {o.badge}
                    </span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function DelayMsInput({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (ms: number | undefined) => void;
}) {
  const [unit, setUnit] = useState<'sec' | 'min' | 'hour' | 'day'>(() => {
    if (!value || value <= 0) return 'min';
    if (value % (24 * 60 * 60 * 1000) === 0) return 'day';
    if (value % (60 * 60 * 1000) === 0) return 'hour';
    if (value % (60 * 1000) === 0) return 'min';
    return 'sec';
  });
  const factor = {
    sec: 1000,
    min: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
  }[unit];
  const [num, setNum] = useState<string>(() =>
    value && value > 0 ? String(value / factor) : '',
  );
  useEffect(() => {
    const n = parseFloat(num);
    if (Number.isFinite(n) && n > 0) {
      onChange(Math.round(n * factor));
    } else {
      onChange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [num, factor]);

  return (
    <div>
      <Label className="text-xs">遅延時間</Label>
      <div className="flex gap-2">
        <Input
          type="number"
          min="0"
          step="any"
          value={num}
          onChange={(e) => setNum(e.target.value)}
          placeholder="0"
          className="flex-1"
        />
        <Select value={unit} onValueChange={(v) => setUnit(v as typeof unit)}>
          <SelectTrigger className="w-[100px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sec">秒</SelectItem>
            <SelectItem value="min">分</SelectItem>
            <SelectItem value="hour">時間</SelectItem>
            <SelectItem value="day">日</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-[10px] text-[var(--text-muted)] mt-1">
        実際の発火（after_delay）は Phase 23b の Cloud Tasks で実装予定。
      </p>
    </div>
  );
}

/**
 * CTA URL 編集 UI（plan adaptive-crown §機能 2）
 * 直接 URL 入力 + ランディング選択 + フォーム選択の 3 モード。
 * 配信時に sendIndividualMailTask が cid (or lid) を自動付与する。
 */
function CtaUrlEditor({
  value,
  onChange,
  campaignId,
  landings,
  forms,
}: {
  value: string;
  onChange: (v: string) => void;
  campaignId: string;
  landings: Array<{ id: string; title: string; slug: string; status: string }>;
  forms: Array<{ id: string; name: string }>;
}) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return (
    <div className="border border-[var(--border)] rounded-md p-2 bg-[var(--bg-elevated)] space-y-2">
      <Label className="text-xs">CTA URL（本文 {`{{CTA_URL}}`} 上書き）</Label>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://... または下のリストから自動生成"
        className="font-mono text-[11px]"
      />
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <Label className="text-[10px] text-[var(--text-muted)]">
            ランディングを選択
          </Label>
          <select
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (!id) return;
              const l = landings.find((x) => x.id === id);
              if (l?.slug) {
                onChange(`${origin}/l/${l.slug}?campaignId=${campaignId}`);
              }
            }}
            className="w-full text-xs px-2 py-1.5 border border-[var(--border)] rounded bg-[var(--bg-input)] text-[var(--text-primary)]"
          >
            <option value="">— 選択 —</option>
            {landings
              .filter((l) => l.status === 'published')
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] text-[var(--text-muted)]">
            フォームを選択
          </Label>
          <select
            value=""
            onChange={(e) => {
              const id = e.target.value;
              if (!id) return;
              onChange(`${origin}/public/forms/${id}?campaignId=${campaignId}`);
            }}
            className="w-full text-xs px-2 py-1.5 border border-[var(--border)] rounded bg-[var(--bg-input)] text-[var(--text-primary)]"
          >
            <option value="">— 選択 —</option>
            {forms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
        配信時、各受信者ごとに <code>?cid={'{contactId}'}</code> が自動付与され、
        ランディング/フォーム送信が紐付いた audience として記録されます。
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────
// referral カード編集 UI
// ─────────────────────────────────────────────
function ReferralCardEditor({
  draft,
  update,
  landings,
  forms,
  campaignId,
}: {
  draft: CampaignElement;
  update: (patch: Partial<CampaignElement>) => void;
  landings: Array<{ id: string; title: string; slug: string; status: string }>;
  forms: Array<{ id: string; name: string }>;
  campaignId: string;
}) {
  const kind = draft.referralTargetKind ?? 'landing';
  const refTag = draft.referralRefTag ?? '';
  const tagValid = !refTag || REFERRAL_TAG_RE.test(refTag);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const handleKindChange = (k: 'landing' | 'form') => {
    // 種別変更時は refId / refLabel をクリア（互換性のないリソースを選び直し）
    update({
      referralTargetKind: k,
      refId: undefined,
      refLabel: '',
      externalUrl: undefined,
    });
  };

  const handleResourceChange = (id: string) => {
    if (!id) {
      update({ refId: undefined, refLabel: '', externalUrl: undefined });
      return;
    }
    if (kind === 'landing') {
      const l = landings.find((x) => x.id === id);
      if (l) {
        update({
          refId: id,
          refLabel: l.title,
          externalUrl: l.slug ? `${origin}/l/${l.slug}` : undefined,
        });
      }
    } else {
      const f = forms.find((x) => x.id === id);
      if (f) {
        update({
          refId: id,
          refLabel: f.name,
          externalUrl: `${origin}/public/forms/${id}`,
        });
      }
    }
  };

  // 生成 URL（プレビュー用、保存はしない）
  let generatedUrl: string | null = null;
  if (draft.refId && refTag && tagValid) {
    let path = '';
    if (kind === 'landing') {
      const l = landings.find((x) => x.id === draft.refId);
      if (l?.slug) path = `/l/${l.slug}`;
    } else {
      path = `/public/forms/${draft.refId}`;
    }
    if (path) {
      const params: Array<[string, string]> = [];
      if (campaignId) params.push(['campaignId', campaignId]);
      params.push(['src', 'referral']);
      params.push(['ref', refTag]);
      const qs = params
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
      generatedUrl = `${origin}${path}?${qs}`;
    }
  }

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      toast.success('URL をコピーしました');
    } catch {
      toast.error('コピー失敗');
    }
  };

  const targetOpts: ResourceOption[] =
    kind === 'landing'
      ? landings
          .filter((l) => l.status === 'published' && l.slug)
          .map((l) => ({ id: l.id, label: l.title }))
      : forms.map((f) => ({ id: f.id, label: f.name }));

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">遷移先の種別</Label>
        <div className="flex gap-2 mt-1">
          <Button
            type="button"
            size="sm"
            variant={kind === 'landing' ? 'primary' : 'ghost'}
            onClick={() => handleKindChange('landing')}
          >
            ランディング
          </Button>
          <Button
            type="button"
            size="sm"
            variant={kind === 'form' ? 'primary' : 'ghost'}
            onClick={() => handleKindChange('form')}
          >
            フォーム
          </Button>
        </div>
      </div>

      <div>
        <Label className="text-xs">
          {kind === 'landing' ? 'ランディングを選択' : 'フォームを選択'}
        </Label>
        <Select
          value={draft.refId ?? NONE_VALUE}
          onValueChange={(v) => handleResourceChange(v === NONE_VALUE ? '' : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="選択してください" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>（未設定）</SelectItem>
            {targetOpts.map((o) => (
              <SelectItem key={o.id} value={o.id}>
                {o.label || `(無題) ${o.id.slice(0, 6)}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <RefTagInput
        id="card-reftag"
        label="REFERRAL タグ（自由記述またはプリセット選択）"
        value={refTag}
        onChange={(v) => update({ referralRefTag: v })}
        onPresetSelected={(presetId) =>
          update({ referralPresetId: presetId ?? '' })
        }
      />

      <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-2 space-y-1.5">
        <Label className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
          発行 URL（プレビュー）
        </Label>
        {generatedUrl ? (
          <>
            <div className="font-mono text-[11px] break-all">{generatedUrl}</div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => void handleCopy()}
            >
              <Copy className="size-3.5 mr-1" />
              URL をコピー
            </Button>
          </>
        ) : (
          <p className="text-[11px] text-[var(--text-muted)]">
            遷移先とチャネルを選択すると URL が生成されます。
          </p>
        )}
      </div>
      <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
        この URL を SNS 広告 / QR / 紙媒体に配置すると、流入が
        <code className="mx-1">audience_events.source='referral'</code>
        +
        <code className="mx-1">metadata.refTag={refTag || '?'}</code>
        として記録され、キャンペーン CV 集計に独立した系統として現れます。
        campaignId は本キャンペーン ID（{campaignId}）が自動付与されます。
      </p>
    </div>
  );
}

export function CardDetailDrawer({
  open,
  onOpenChange,
  element,
  campaignId,
  hasIncoming,
  allElements,
  allConnections,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<CampaignElement | null>(element);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [creatingNews, setCreatingNews] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const createLineNewsMutation = useCreateLineNews();

  // 各リソースを取得（Drawer が開いてから fetch されるよう React Query キャッシュは有効）
  const formsQ = useForms();
  const lineNewsQ = useLineNewsList({ limit: 200 });
  const landingsQ = useLandings();
  const documentsQ = useDocuments();
  const mailSchedulesQ = useMailSchedules();
  const mailsQ = useMails({ limit: 200 });
  const mailGroupsQ = useMailGroups();
  const eventsQ = useEvents(200);
  const mailAccountsQ = useMailAccounts();
  const liffSettingsQ = useLiffSettings();
  const activeMailAccounts = (mailAccountsQ.data ?? []).filter(
    (a) => a.status === 'active',
  );
  const defaultMailAccount =
    activeMailAccounts.find((a) => a.isDefault) ?? activeMailAccounts[0] ?? null;

  useEffect(() => {
    if (open && element) {
      setDraft(element);
    }
  }, [open, element]);

  // mail_send カード初回オープン時、fromAccountId が未設定なら既定アカウントを自動セット（Phase 26）
  useEffect(() => {
    if (!open || !draft || draft.type !== 'mail_send') return;
    if (draft.fromAccountId) return;
    if (!defaultMailAccount) return;
    setDraft((d) =>
      d
        ? {
            ...d,
            fromAccountId: defaultMailAccount.id,
            fromAddress: defaultMailAccount.email,
          }
        : d,
    );
  }, [open, draft, defaultMailAccount]);

  // B（自動 CTA URL）: mail/LINE カードを開いた時、ctaUrlOverride が空なら
  // 下流の最寄り landing/form の URL を suggested として自動セットする。
  // 起源カード ID が変わるたびに 1 回だけ実行（user が空にしたら以降上書きしない）。
  useEffect(() => {
    if (!open || !draft) return;
    const supportsCta =
      draft.type === 'line_news' ||
      draft.type === 'mail_send' ||
      draft.type === 'mail_schedule';
    if (!supportsCta) return;
    // すでに値があるなら上書きしない（手動指定を尊重）
    if (draft.ctaUrlOverride && draft.ctaUrlOverride.trim()) return;
    if (!allElements || !allConnections) return;

    // BFS で下流の最寄り landing or form を探索
    const elementsById = new Map(allElements.map((e) => [e.id, e]));
    const adj = new Map<string, string[]>();
    for (const c of allConnections) {
      const arr = adj.get(c.fromId) ?? [];
      arr.push(c.toId);
      adj.set(c.fromId, arr);
    }
    const seen = new Set<string>([draft.id]);
    const queue: string[] = [draft.id];
    let suggested: { url: string; ref: string } | null = null;
    while (queue.length > 0 && !suggested) {
      const cur = queue.shift()!;
      for (const next of adj.get(cur) ?? []) {
        if (seen.has(next)) continue;
        seen.add(next);
        const el = elementsById.get(next);
        if (!el) continue;
        if (el.type === 'landing' && el.refId) {
          // landing は slug が必要。useLandings から逆引き
          const lands = landingsQ.data ?? [];
          const matched = lands.find((l) => l.id === el.refId);
          if (matched?.slug) {
            suggested = {
              url: `${window.location.origin}/l/${matched.slug}?campaignId=${campaignId}`,
              ref: matched.id,
            };
            break;
          }
        } else if (el.type === 'form' && el.refId) {
          suggested = {
            url: `${window.location.origin}/public/forms/${el.refId}?campaignId=${campaignId}`,
            ref: el.refId,
          };
          break;
        }
        queue.push(next);
      }
    }
    if (suggested) {
      setDraft((d) =>
        d && (!d.ctaUrlOverride || !d.ctaUrlOverride.trim())
          ? { ...d, ctaUrlOverride: suggested!.url }
          : d,
      );
    }
    // draft.id が変わったとき（別カード選択）にだけ再走させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft?.id, allElements, allConnections, landingsQ.data, campaignId]);

  if (!draft) return null;

  const update = (patch: Partial<CampaignElement>) => {
    // 関数型 setState を使用しないと、1 イベント内で update() が複数回呼ばれた時に
    // 最初の patch が消える（clopsure 内の draft が古いため）。
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };

  const handleResourceSelect = (id: string | undefined, label: string) => {
    let externalUrl: string | undefined = undefined;
    if (draft.type === 'form' && id) {
      externalUrl = `${window.location.origin}/public/forms/${id}?campaignId=${campaignId}`;
    } else if (draft.type === 'landing' && id) {
      // 紐付けたランディングの公開 URL（slug 経由、campaignId 自動付与）
      const landing = landingsQ.data?.find((l) => l.id === id);
      const slug = landing?.slug;
      if (slug) {
        externalUrl = `${window.location.origin}/l/${slug}?campaignId=${campaignId}`;
      }
    } else if (draft.type === 'document_request' && id) {
      // 公開資料 URL（slug ベース、campaignId + src=referral + ref=document_request）
      const document = documentsQ.data?.find((d) => d.id === id);
      const slug = document?.slug;
      if (slug) {
        externalUrl = `${window.location.origin}/docs/${slug}?campaignId=${campaignId}&src=referral&ref=document_request`;
      }
    } else if (
      (draft.type === 'briefing_request' || draft.type === 'quote_request') &&
      id
    ) {
      // 営業フォーム公開 URL（campaignId + src=referral + ref=briefing/quote_request）
      const refTag =
        draft.type === 'briefing_request' ? 'briefing_request' : 'quote_request';
      externalUrl = `${window.location.origin}/public/forms/${id}?campaignId=${campaignId}&src=referral&ref=${refTag}`;
    }
    update({ refId: id, refLabel: label, externalUrl });
  };

  const handleSave = () => {
    onSave(draft);
    onOpenChange(false);
  };

  // type 別のリソース select
  let body: React.ReactNode = null;
  if (draft.type === 'form') {
    const opts: ResourceOption[] = (formsQ.data ?? []).map((f) => ({
      id: f.id,
      label: f.name,
    }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="フォームがありません。「フォーム」画面から作成してください。"
      />
    );
  } else if (draft.type === 'landing') {
    const opts: ResourceOption[] = (landingsQ.data ?? []).map((l) => ({
      id: l.id,
      label: `${l.title}${l.status === 'published' ? '' : '（下書き）'}`,
    }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="ランディングがありません。「ランディング」画面から作成してください。"
      />
    );
  } else if (draft.type === 'line_news') {
    const allNews = lineNewsQ.data ?? [];
    const currentNews = allNews.find((n) => n.id === draft.refId);
    // 新規選択は draft のみ。ただし現在紐付いている news は status を問わず
    // バッジ + disabled 状態で常に表示（予約済になっても「何が紐付いていたか」が分かるようにする UX）。
    const draftOpts: ResourceOption[] = allNews
      .filter((n) => n.status === 'draft')
      .map((n) => ({ id: n.id, label: n.title || '(無題)' }));
    const opts: ResourceOption[] =
      currentNews && currentNews.status !== 'draft'
        ? [
            {
              id: currentNews.id,
              label: currentNews.title || '(無題)',
              badge: currentNews.status,
              disabled: true,
            },
            ...draftOpts,
          ]
        : draftOpts;

    const handleCreateLineNewsDraft = async () => {
      if (!campaignId) {
        toast.error('キャンペーンを保存してから新規 NEWS を作成してください');
        return;
      }
      setCreatingNews(true);
      try {
        const newId = await createLineNewsMutation.mutateAsync({
          title: '無題',
          sections: [],
          status: 'draft',
          createdBy: user?.uid ?? '',
        });
        // line_news.campaignIds[] にバックグラウンドで campaignId を付与
        await safeUpdateDoc(doc(db, 'line_news', newId), {
          campaignIds: arrayUnion(campaignId),
        });
        // ボードの draft 状態にも反映してから保存（即時 onSave）
        const next: CampaignElement = {
          ...draft,
          refId: newId,
          refLabel: draft.refLabel || '無題',
        };
        onSave(next);
        onOpenChange(false);
        navigate(`/line/news?editingId=${newId}`);
      } catch (err) {
        toast.error(`作成失敗: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setCreatingNews(false);
      }
    };

    body = (
      <div className="space-y-3">
        <ResourceSelector
          type={draft.type}
          value={draft.refId}
          onChange={handleResourceSelect}
          options={opts}
          emptyMessage="下書き状態の LINE ニュースがありません。下のボタンから新規作成してください。"
        />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void handleCreateLineNewsDraft()}
          disabled={creatingNews}
          className="!justify-start !w-full !text-[var(--accent)] hover:!bg-[var(--accent-subtle)]"
        >
          <Plus className="size-3.5 mr-1" />
          {creatingNews ? '作成中…' : '新規 NEWS を作成して編集画面へ'}
        </Button>

        <CtaUrlEditor
          value={draft.ctaUrlOverride ?? ''}
          onChange={(v) => update({ ctaUrlOverride: v })}
          campaignId={campaignId}
          landings={landingsQ.data ?? []}
          forms={formsQ.data ?? []}
        />
        <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          指定すると、配信時 LINE 本文の CTA URL（{`{{CTA_URL}}`} およびセクション末尾の URL）を上書きします。空欄ならニュース編集画面で設定したセクション既定 URL が使われます。
        </p>

        {draft.refId && currentNews?.status === 'draft' && (
          <ScheduleSendAtBlock
            newsId={draft.refId}
            currentSendAt={currentNews.sendAt}
            targetUserIds={currentNews.targetUserIds}
            campaignId={campaignId}
            ctaUrlOverride={draft.ctaUrlOverride ?? null}
          />
        )}

        {draft.refId && currentNews && currentNews.status !== 'draft' && (
          <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
            このニュースは「{currentNews.status}」状態のためカード内から再予約できません。
            「LINE ニュース」画面で「下書きに戻す」操作をしてください。
          </p>
        )}

        <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          下書きから配信予約日時を設定すると、当該ニュースの sendAt が更新されて配信予約状態となり、選択肢からは外れます。
        </p>
      </div>
    );
  } else if (draft.type === 'mail_schedule') {
    // incoming connection の有無で選択肢を切り替え：
    //   - incoming あり (中間カード): targetType='addresses' のみ（dispatcher が email を arrayUnion）
    //   - incoming なし (起点カード): 全 targetType（独立予約として動く）
    const allSchedules = mailSchedulesQ.data ?? [];
    const filtered = hasIncoming
      ? allSchedules.filter((s) => s.targetType === 'addresses')
      : allSchedules;
    const opts: ResourceOption[] = filtered.map((s) => {
      const typeBadge =
        s.targetType === 'addresses'
          ? '直接'
          : s.targetType === 'group'
            ? 'G'
            : 'タグ';
      return {
        id: s.id,
        label: `[${typeBadge}] ${s.subject || '(件名なし)'}`,
      };
    });
    body = (
      <div className="space-y-2">
        <ResourceSelector
          type={draft.type}
          value={draft.refId}
          onChange={handleResourceSelect}
          options={opts}
          emptyMessage={
            hasIncoming
              ? '直接アドレス型の配信予約がありません。下のボタンから新規作成（自動で addresses 型）してください。'
              : '配信予約がまだありません。下のボタンから新規作成できます。'
          }
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setScheduleDialogOpen(true)}
          className="!justify-start !w-full !text-[var(--accent)] hover:!bg-[var(--accent-subtle)]"
        >
          <Plus className="size-3.5 mr-1" />
          新規配信予約をこのカードから作成
        </Button>
        <CtaUrlEditor
          value={draft.ctaUrlOverride ?? ''}
          onChange={(v) => update({ ctaUrlOverride: v })}
          campaignId={campaignId}
          landings={landingsQ.data ?? []}
          forms={formsQ.data ?? []}
        />
        <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          {hasIncoming
            ? '上流のカードから audience が流れ込むため、直接アドレス型の予約のみ表示されています。'
            : '起点カードのため、グループ／タグ／直接アドレス型のすべての予約を選択できます。'}
        </p>
      </div>
    );
  } else if (draft.type === 'mail_send') {
    // dispatcher が新規 schedule を動的作成するためのカード（ナーチャリング型）
    const mailOpts: ResourceOption[] = (mailsQ.data ?? []).map((m) => ({
      id: m.id,
      label: m.subject || '(件名なし)',
    }));
    body = (
      <div className="space-y-3">
        <ResourceSelector
          type={draft.type}
          value={draft.refId}
          onChange={handleResourceSelect}
          options={mailOpts}
          emptyMessage="送信用メールがありません。「メールリスト」画面から作成してください。"
        />
        <div>
          <Label className="text-xs">送信元アドレス</Label>
          {activeMailAccounts.length === 0 ? (
            <p className="text-[11px] text-[var(--accent-amber)]">
              送信元アドレスが未登録です。「メール設定」画面から追加してください。
            </p>
          ) : (
            <Select
              value={draft.fromAccountId ?? ''}
              onValueChange={(v) => {
                const acc = activeMailAccounts.find((a) => a.id === v);
                update({
                  fromAccountId: v,
                  fromAddress: acc?.email ?? draft.fromAddress,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="送信元を選択" />
              </SelectTrigger>
              <SelectContent>
                {activeMailAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.displayName} &lt;{a.email}&gt;{a.isDefault ? ' ★既定' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <CtaUrlEditor
          value={draft.ctaUrlOverride ?? ''}
          onChange={(v) => update({ ctaUrlOverride: v })}
          campaignId={campaignId}
          landings={landingsQ.data ?? []}
          forms={formsQ.data ?? []}
        />
        <div>
          <Label className="text-xs">送信控え（任意・複数可）</Label>
          <EmailChipsInput
            value={draft.cc ?? []}
            onChange={(next) => update({ cc: next })}
            placeholder="控えを受け取るメアドを入力（Enter / カンマ で確定）"
          />
          <p className="mt-1 text-[10px] text-[var(--text-muted)] leading-relaxed">
            配信ごとに自動作成される予約に引き継がれ、配信内容の控えが指定アドレスへ 1 通だけ届きます（件名に【送信控え】が付きます）。
          </p>
        </div>
        <p className="text-[10px] text-[var(--text-muted)] leading-relaxed">
          dispatcher 起動ごとに新規 schedule を自動作成し、audience に個別配信します（ナーチャリング型）。
          線の <code>after_delay 3 日</code> を設定すれば、上流カード完了から 3 日後に送信されます。
        </p>
      </div>
    );
  } else if (draft.type === 'mail_draft') {
    const opts: ResourceOption[] = (mailsQ.data ?? []).map((m) => ({
      id: m.id,
      label: m.subject || '(件名なし)',
    }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="メール下書きがありません。「メールリスト」画面から作成してください。"
      />
    );
  } else if (draft.type === 'mail_contacts_group') {
    const opts: ResourceOption[] = (mailGroupsQ.data ?? []).map((g) => ({
      id: g.id,
      label: g.name,
    }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="配信先グループがありません。「配信先管理」画面から作成してください。"
      />
    );
  } else if (draft.type === 'event') {
    const opts: ResourceOption[] = (eventsQ.data ?? []).map((e) => ({
      id: e.id,
      label: e.name || '(無題)',
    }));
    const startAtLocal = toDatetimeLocalValue(draft.eventStartAt ?? null);
    const endAtLocal = toDatetimeLocalValue(draft.eventEndAt ?? null);
    const localToTs = (v: string): IsoOrTimestamp | null => {
      if (!v) return null;
      const ms = new Date(v).getTime();
      if (Number.isNaN(ms)) return null;
      return Timestamp.fromMillis(ms);
    };
    body = (
      <div className="space-y-3">
        <ResourceSelector
          type={draft.type}
          value={draft.refId}
          onChange={(id, label) => {
            // 既存 Event を選択した場合、eventDate を eventStartAt に自動コピー（未設定時のみ）
            const evt = eventsQ.data?.find((e) => e.id === id);
            let autoStart: IsoOrTimestamp | null | undefined = draft.eventStartAt;
            if (id && evt && !draft.eventStartAt) {
              autoStart = evt.eventDate ?? null;
            }
            update({
              refId: id,
              refLabel: label,
              eventStartAt: autoStart,
              // 発火状態は新リソース選択でリセット
              lastEventStartFiredAt: null,
              lastEventEndFiredAt: null,
            });
          }}
          options={opts}
          emptyMessage="イベントがありません。「イベント管理」画面から作成してください。"
        />
        <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-3">
          <div>
            <Label className="text-xs font-semibold !mb-0">自動トリガー設定</Label>
            <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
              指定した時刻に達すると、このカードからの接続線が「開始時」「終了時」トリガーで自動発火します。空欄なら手動実行のみ。
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">開催開始</Label>
              <Input
                type="datetime-local"
                value={startAtLocal}
                onChange={(e) =>
                  update({
                    eventStartAt: localToTs(e.target.value),
                    // 時刻編集時は発火フラグをクリア（再発火可能に）
                    lastEventStartFiredAt: null,
                  })
                }
              />
              {draft.lastEventStartFiredAt && (
                <p className="text-[10px] text-[var(--accent-green)] mt-1">
                  ✓ 開始トリガー発火済み
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs">開催終了</Label>
              <Input
                type="datetime-local"
                value={endAtLocal}
                onChange={(e) =>
                  update({
                    eventEndAt: localToTs(e.target.value),
                    lastEventEndFiredAt: null,
                  })
                }
              />
              {draft.lastEventEndFiredAt && (
                <p className="text-[10px] text-[var(--accent-green)] mt-1">
                  ✓ 終了トリガー発火済み
                </p>
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">参加者フォーム（任意）</Label>
            <p className="text-[10px] text-[var(--text-muted)] mb-1">
              紐付けたフォームの送信者を、開始/終了トリガー発火時の audience として下流カード（メール送信等）に自動的に渡します。
            </p>
            <Select
              value={draft.participantFormId ?? '__none__'}
              onValueChange={(v) =>
                update({
                  participantFormId: v === '__none__' ? undefined : v,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="フォームを選択（任意）" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">紐付けなし</SelectItem>
                {(formsQ.data ?? []).map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  } else if (draft.type === 'referral') {
    body = (
      <ReferralCardEditor
        draft={draft}
        update={update}
        landings={landingsQ.data ?? []}
        forms={formsQ.data ?? []}
        campaignId={campaignId}
      />
    );
  } else if (draft.type === 'document_request') {
    // 資料請求: 公開資料のみ表示（draft / archived は除外）
    const opts: ResourceOption[] = (documentsQ.data ?? [])
      .filter((d) => d.status === 'published')
      .map((d) => ({
        id: d.id,
        label: `${d.title}${d.passwordHash ? ' 🔒' : ''}`,
      }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="公開中の資料がありません。「資料管理」画面で作成し、状態を「公開中」にしてください。"
      />
    );
  } else if (draft.type === 'briefing_request') {
    // 個別説明申込: dealStageHint='briefing' の営業フォームを抽出
    const opts: ResourceOption[] = (formsQ.data ?? [])
      .filter((f) => f.dealStageHint === 'briefing')
      .map((f) => ({ id: f.id, label: f.name }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="個別説明用フォームがありません。「フォーム」画面で dealStageHint=briefing のフォームを作成してください。"
      />
    );
  } else if (draft.type === 'quote_request') {
    // 見積依頼: dealStageHint='quote' の営業フォームを抽出
    const opts: ResourceOption[] = (formsQ.data ?? [])
      .filter((f) => f.dealStageHint === 'quote')
      .map((f) => ({ id: f.id, label: f.name }));
    body = (
      <ResourceSelector
        type={draft.type}
        value={draft.refId}
        onChange={handleResourceSelect}
        options={opts}
        emptyMessage="見積依頼用フォームがありません。「フォーム」画面で dealStageHint=quote のフォームを作成してください。"
      />
    );
  } else if (draft.type === 'delay') {
    body = (
      <DelayMsInput
        value={draft.delayMs}
        onChange={(ms) => update({ delayMs: ms })}
      />
    );
  } else if (draft.type === 'note') {
    body = (
      <div>
        <Label className="text-xs">メモ</Label>
        <Textarea
          value={draft.note ?? ''}
          onChange={(e) => update({ note: e.target.value })}
          rows={5}
          placeholder="ホワイトボードへの自由記述メモ"
        />
      </div>
    );
  } else {
    // condition / metric
    body = (
      <div className="rounded border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-xs text-[var(--text-secondary)]">
        この種別の詳細編集は Phase 23b で実装予定です。
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {TYPE_LABEL[draft.type] ?? draft.type} カードの設定
          </DialogTitle>
          <DialogDescription>
            既存のリソースを紐付けると、ボード保存時にそのリソースの{' '}
            <code>campaignIds[]</code> に自動で追加されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {body}

          {/* 表示名（任意上書き） */}
          {!['delay', 'note', 'condition', 'metric'].includes(draft.type) && (
            <div>
              <Label className="text-xs">カード表示名（任意）</Label>
              <Input
                type="text"
                value={draft.refLabel ?? ''}
                onChange={(e) => update({ refLabel: e.target.value })}
                placeholder="未指定なら選択リソース名を表示"
              />
            </div>
          )}

          {/* form 専用: 通常 URL + LIFF URL の 2 行表示。それ以外は従来通り 1 行 */}
          {draft.type === 'form' && draft.refId ? (
            <div className="space-y-2 pt-1">
              {draft.externalUrl && (
                <div>
                  <Label className="text-[10px] text-[var(--text-muted)]">
                    フォームリンク（通常 URL）
                  </Label>
                  <div className="flex items-center gap-2 text-[11px] truncate">
                    <ExternalLink className="size-3 flex-shrink-0 text-[var(--text-muted)]" />
                    <a
                      href={draft.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono truncate text-[var(--accent)] hover:underline"
                    >
                      {draft.externalUrl}
                    </a>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-[10px] text-[var(--text-muted)]">
                  LINE 用フォームリンク（LIFF）
                </Label>
                {liffSettingsQ.data?.liffId ? (
                  (() => {
                    const liffUrl = `https://liff.line.me/${liffSettingsQ.data.liffId}?formId=${encodeURIComponent(draft.refId)}&campaignId=${encodeURIComponent(campaignId)}`;
                    return (
                      <div className="flex items-center gap-2 text-[11px] truncate">
                        <ExternalLink className="size-3 flex-shrink-0 text-[#06c755]" />
                        <a
                          href={liffUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono truncate text-[#06c755] hover:underline"
                        >
                          {liffUrl}
                        </a>
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-[11px] text-[var(--text-muted)] italic">
                    LIFF ID 未設定。「設定 → 一般設定 → LIFF 設定」で登録してください。
                  </p>
                )}
              </div>
            </div>
          ) : (
            draft.externalUrl && (
              <div className="flex items-center gap-2 text-[11px] text-[var(--text-muted)] truncate">
                <ExternalLink className="size-3 flex-shrink-0" />
                <span className="font-mono truncate">{draft.externalUrl}</span>
              </div>
            )
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSave}
          >
            適用
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* 新規配信予約ダイアログ（mail_schedule カード経由で起動） */}
      <ScheduleFormDialog
        open={scheduleDialogOpen}
        onOpenChange={setScheduleDialogOpen}
        initialTargetType="addresses"
        contextHint="キャンペーンボードから作成中です。送信先タイプを「直接アドレス」にすると、フォーム送信者などの audience が自動で追加されます。"
        onScheduled={(result) => {
          // 作成済 schedule を refId に自動セット + Mail 一覧から subject を取り出して refLabel に
          const newId = result.scheduleId;
          // subject は invalidate 後に取得されるため、暫定的に「新規予約」をラベルに
          // ボード保存後に再描画されたとき、CardDetailDrawer 再表示で正式 subject が出る
          setDraft((d) =>
            d
              ? {
                  ...d,
                  refId: newId,
                  refLabel: d.refLabel || '（新規予約）',
                }
              : d,
          );
          setScheduleDialogOpen(false);
        }}
      />
    </Dialog>
  );
}

/**
 * line_news draft に対して sendAt を設定するブロック。
 * useScheduleLineNewsSend を呼ぶと、line_news_schedules ドキュメント作成 +
 * line_news.sendAt 更新 + status='scheduled' 化が一括で行われる。
 */
function ScheduleSendAtBlock({
  newsId,
  currentSendAt,
  targetUserIds,
  campaignId,
  ctaUrlOverride,
}: {
  newsId: string;
  currentSendAt: IsoOrTimestamp | null;
  targetUserIds: string[];
  campaignId?: string;
  /** カード経由予約時の CTA URL 上書き（任意） */
  ctaUrlOverride?: string | null;
}) {
  const scheduleMutation = useScheduleLineNewsSend();
  const [value, setValue] = useState(toDatetimeLocalValue(currentSendAt));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(toDatetimeLocalValue(currentSendAt));
  }, [currentSendAt]);

  const handleSave = async () => {
    if (!value) {
      toast.error('日時を入力してください');
      return;
    }
    const sendAtMs = new Date(value).getTime();
    if (Number.isNaN(sendAtMs)) {
      toast.error('日時の形式が不正です');
      return;
    }
    if (sendAtMs <= Date.now()) {
      toast.error('未来の日時を指定してください');
      return;
    }
    setSaving(true);
    try {
      await scheduleMutation.mutateAsync({
        newsId,
        sendAt: new Date(sendAtMs).toISOString(),
        targetUserIds,
        campaignId: campaignId ?? null,
        ctaUrlOverride: ctaUrlOverride ?? null,
      });
      toast.success('配信予約を設定しました');
    } catch (err) {
      toast.error(`予約失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded border border-[var(--border)] p-2 space-y-1.5">
      <Label className="text-xs">配信予約日時</Label>
      <div className="flex gap-2">
        <Input
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1"
        />
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void handleSave()}
          disabled={saving || !value}
        >
          {saving ? '保存中…' : '予約'}
        </Button>
      </div>
      <p className="text-[10px] text-[var(--text-muted)]">
        予約完了後、ニュースは「予約済」となり選択肢から外れます。再編集は LINE ニュース画面で「下書きに戻す」を使用してください。
      </p>
    </div>
  );
}
